import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { LuminariasService } from '../../../service';
import {
  IConfigDeseada,
  IConfigDeseadaLuminariaGPE,
  IConfigDeseadaLuminariaACTIS,
  IConfigDeseadaLuminariaWellness,
  IDispositivoLuminariaGPE,
  IDispositivoLuminariaACTIS,
  IDispositivoLuminariaWellness,
  IConfigGeneralGPE,
  IDimmerCalendarioConfig,
  ICambioDimmingACTIS,
  IModosACTIS,
} from 'modelos/src';
import { MaterialModule } from '../../../../../../auxiliares/material.module';
import { EmptyStateComponent } from '../../../../../../componentes/empty-state/empty-state.component';
import { ContentCardComponent } from '../../../../../../componentes/content-card/content-card.component';
import { ILuminariaExtendida } from '../../../luminarias.types';
import {
  DimmingChartComponent,
  IPuntoDimmingChart,
} from '../../../../../standalone/config-perfil/formularios/dimming-chart.component';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../../componentes/map-card/map-card.component';
import { MarkerMapa } from '../../../../../../auxiliares/mapa-facil/interfaces';

// Fuente del criterio de calendario de un slot. Los perfiles «todos los días»
// no tienen ninguno (undefined); los de «días seleccionados» traen diasSemana y
// los de «día especial» traen diaDelAnio.
type FuenteCalendario =
  | { diasSemana?: boolean[]; diaDelAnio?: number; }
  | undefined;

export interface PerfilDimmingSlot {
  etiqueta: string;
  bit: number;
  habilitadoReal: boolean;
  habilitadoDeseado: boolean | null;
  puntosReal: IPuntoDimmingChart[];
  puntosDeseado: IPuntoDimmingChart[];
  diferente: boolean;
  tieneDatos: boolean;
  // Criterio de calendario del slot. Un perfil no es solo su curva: los de tipo
  // «días seleccionados» llevan además qué días aplican y los de «día especial»
  // qué día del año. Esos campos también divergen y hasta ahora no se veían.
  tipoCalendario: 'todosLosDias' | 'diasSemana' | 'diaEspecial';
  diasSemanaReal: boolean[] | null;
  diasSemanaDeseado: boolean[] | null;
  diasSemanaDiferente: boolean;
  diaDelAnioReal: number | null;
  diaDelAnioDeseado: number | null;
  diaDelAnioDiferente: boolean;
}

@Component({
  selector: 'app-detalles-perfiles',
  templateUrl: './detalles-perfiles.component.html',
  styleUrls: ['./detalles-perfiles.component.scss'],
  imports: [
    MaterialModule,
    EmptyStateComponent,
    ContentCardComponent,
    DimmingChartComponent,
    MapCardComponent,
  ],
})
export class DetallesPerfilesComponent implements OnChanges {
  private readonly luminariasService = inject(LuminariasService);

  @Input() configDeseada: IConfigDeseada | null = null;
  @Input() luminaria: ILuminariaExtendida | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['luminaria'] || changes['configDeseada']) {
      this._mapCfg = this.buildMapCfg();
    }
  }

  // ─── tipo ───────────────────────────────────────────────────

  get tipoDispositivo(): string | undefined {
    // Device manda; fallback al tipo de la config deseada si el device no lo trae.
    return this.luminaria?.dispositivo?.tipo ?? this.configDeseada?.tipo;
  }

  // ─── configs reales ─────────────────────────────────────────

  get configRealGPE(): IDispositivoLuminariaGPE | null {
    if (this.tipoDispositivo !== 'Luminaria GPE') return null;
    return (
      (this.luminaria?.dispositivo?.config as IDispositivoLuminariaGPE) ?? null
    );
  }

  get configRealACTIS(): IDispositivoLuminariaACTIS | null {
    if (this.tipoDispositivo !== 'Luminaria ACTIS FING') return null;
    const cfg =
      (this.luminaria?.dispositivo?.config as IDispositivoLuminariaACTIS) ??
      null;
    const pd = cfg?.perfilesDimming;
    if (!pd || pd.perfilesHabilitados == null) return cfg;
    // Normaliza la máscara REAL igual que la deseada (limpia bits de perfiles
    // sin curva). Si no, un bit prendido de un perfil vacío en el device hace
    // que hayDiscrepancias()/% marquen una diferencia que los slots suprimen
    // (guard tieneCurva) → banner "no coincide" sin ningún ⚠️ visible.
    const mask = this.mascaraHabilitadosEfectiva(pd);
    if (mask === (pd.perfilesHabilitados & 0xff)) return cfg;
    return { ...cfg, perfilesDimming: { ...pd, perfilesHabilitados: mask } };
  }

  get configRealWellness(): IDispositivoLuminariaWellness | null {
    if (this.tipoDispositivo !== 'Luminaria Wellness') return null;
    return (
      (this.luminaria?.dispositivo?.config as IDispositivoLuminariaWellness) ??
      null
    );
  }

  get configRealGeneralGPE(): IConfigGeneralGPE | null {
    return this.configRealGPE?.configGeneral ?? null;
  }

  get configRealCalendarioGPE(): IDimmerCalendarioConfig | null {
    return this.configRealGPE?.configCalendario ?? null;
  }

  // ─── configs deseadas ────────────────────────────────────────

  // Las getters de config deseada se gatean por el tipo del DISPOSITIVO (fuente
  // autoritativa), no por `configDeseada.tipo`: ese discriminante puede venir
  // ausente/desfasado en documentos viejos, lo que ocultaba los valores deseados
  // (p. ej. en ACTIS se mostraban solo los reales). El tipo del device manda.
  get configDeseadaGPE(): IConfigDeseadaLuminariaGPE | null {
    if (this.tipoDispositivo !== 'Luminaria GPE') return null;
    return (this.configDeseada?.config as IConfigDeseadaLuminariaGPE) ?? null;
  }

  get configDeseadaACTIS(): IConfigDeseadaLuminariaACTIS | null {
    if (this.tipoDispositivo !== 'Luminaria ACTIS FING') return null;
    let cfg =
      (this.configDeseada?.config as IConfigDeseadaLuminariaACTIS) ?? null;
    if (!cfg) return null;
    // Normaliza: un perfil SIN curva no puede quedar habilitado en el device
    // (reporta su bit en 0). Comparar contra la máscara cruda daría una
    // discrepancia irresoluble (en hayDiscrepancias / porcentajeAplicado /
    // slots). Devolvemos un clon con la máscara efectiva (bits de perfiles
    // vacíos limpiados).
    const pd = cfg.perfilesDimming;
    if (pd) {
      cfg = { ...cfg, perfilesDimming: this.normalizarPerfilesDimming(pd) };
    }
    // Coordenadas SIN timeZone resoluble (ni en la deseada ni en el real) = estado
    // válido: sin tz no se puede emitir el frame pto14 (lat+lon+tz atómico) sin
    // clobbear la zona, así que el reconciliador NO lo exige. Replicamos su
    // `normalizarCoordenadasSinTzACTIS`: quitamos coordenadas de la deseada para
    // que no cuente como discrepancia (banner) ni baje el % (front↔back alineado).
    if (cfg.coordenadas) {
      const tz =
        cfg.coordenadas.timeZone ??
        this.configRealACTIS?.coordenadas?.timeZone;
      if (tz == null) {
        const { coordenadas: _omit, ...resto } = cfg;
        cfg = resto;
      }
    }
    return cfg;
  }

  // Máscara perfilesHabilitados con los bits de perfiles sin curva limpiados.
  private mascaraHabilitadosEfectiva(
    pd: NonNullable<IDispositivoLuminariaACTIS['perfilesDimming']>,
  ): number {
    // Índice = bit del perfil (bit0=Todos1 … bit7=Esp4, ver protocolo).
    const tieneCurva = [
      !!pd.perfilTodosLosDias1?.length,
      !!pd.perfilTodosLosDias2?.length,
      !!pd.perfilDiasSemana1?.cambios?.length,
      !!pd.perfilDiasSemana2?.cambios?.length,
      !!pd.perfilDiaEspecial1?.cambios?.length,
      !!pd.perfilDiaEspecial2?.cambios?.length,
      !!pd.perfilDiaEspecial3?.cambios?.length,
      !!pd.perfilDiaEspecial4?.cambios?.length,
    ];
    let mask = (pd.perfilesHabilitados ?? 0) & 0xff;
    for (let bit = 0; bit < 8; bit++) {
      if (!tieneCurva[bit]) mask &= ~(1 << bit) & 0xff;
    }
    return mask;
  }

  // Clon de la DESEADA perfilesDimming: máscara efectiva + sub-perfiles vacíos
  // quitados. El perfil Dimming persiste placeholders de los 8 slots (p.ej.
  // perfilDiasSemana1 = { diasSemana:[7×false], cambios:[] }); como objetos NO
  // los captura `sinValor` en campoDiferente → cuentan como discrepancia
  // irresoluble (banner + %) aunque el device nunca reporta esas estructuras. Un
  // sub-perfil sin curva no aplica nada, así que no se exige. Mismo criterio que
  // el reconciliador (normalizarHabilitadosVaciosACTIS). No muta el original.
  private normalizarPerfilesDimming(
    pd: NonNullable<IDispositivoLuminariaACTIS['perfilesDimming']>,
  ): NonNullable<IDispositivoLuminariaACTIS['perfilesDimming']> {
    const tieneCurva = [
      !!pd.perfilTodosLosDias1?.length,
      !!pd.perfilTodosLosDias2?.length,
      !!pd.perfilDiasSemana1?.cambios?.length,
      !!pd.perfilDiasSemana2?.cambios?.length,
      !!pd.perfilDiaEspecial1?.cambios?.length,
      !!pd.perfilDiaEspecial2?.cambios?.length,
      !!pd.perfilDiaEspecial3?.cambios?.length,
      !!pd.perfilDiaEspecial4?.cambios?.length,
    ];
    const keysPorBit: string[] = [
      'perfilTodosLosDias1',
      'perfilTodosLosDias2',
      'perfilDiasSemana1',
      'perfilDiasSemana2',
      'perfilDiaEspecial1',
      'perfilDiaEspecial2',
      'perfilDiaEspecial3',
      'perfilDiaEspecial4',
    ];
    const clon = { ...pd } as Record<string, unknown>;
    if (pd.perfilesHabilitados != null) {
      clon['perfilesHabilitados'] = this.mascaraHabilitadosEfectiva(pd);
    }
    for (let bit = 0; bit < 8; bit++) {
      if (!tieneCurva[bit]) delete clon[keysPorBit[bit]];
    }
    return clon as NonNullable<IDispositivoLuminariaACTIS['perfilesDimming']>;
  }

  get configDeseadaWellness(): IConfigDeseadaLuminariaWellness | null {
    if (this.tipoDispositivo !== 'Luminaria Wellness') return null;
    return (
      (this.configDeseada?.config as IConfigDeseadaLuminariaWellness) ?? null
    );
  }

  get configDeseadaGeneralGPE(): IConfigGeneralGPE | null {
    return this.configDeseadaGPE?.configGeneral ?? null;
  }

  get configDeseadaCalendarioGPE(): IDimmerCalendarioConfig | null {
    return this.configDeseadaGPE?.configCalendario ?? null;
  }

  // ─── helpers ────────────────────────────────────────────────

  hasVal(v: any): boolean {
    return v !== null && v !== undefined;
  }

  formatVal(v: any): string {
    if (!this.hasVal(v)) return 'Sin información';
    if (typeof v === 'boolean') return v ? 'Habilitado' : 'Deshabilitado';
    return String(v);
  }

  formatCoordenadas(coords: number[] | undefined | null): string {
    if (!coords || coords.length < 2) return 'Sin información';
    return `Lat ${coords[1].toFixed(6)}, Lng ${coords[0].toFixed(6)}`;
  }

  // ─── comparación ────────────────────────────────────────────

  campoDiferente(real: any, deseado: any): boolean {
    // "Sin valor" en la deseada (undefined/null/array vacío) = no exige nada →
    // no es diferencia. Alinea con el diffConfigs del back (ignorarUndefined +
    // no compara lo que la deseada no especifica). Evita ⚠️ espurias por el
    // caso `[]` (deseado) vs `undefined` (real), p.ej. días-semana sin curva.
    const sinValor = (v: any) =>
      v === undefined || v === null || (Array.isArray(v) && v.length === 0);
    if (sinValor(deseado)) return false;

    if (Array.isArray(real) && Array.isArray(deseado)) {
      if (real.length !== deseado.length) return true;
      for (let i = 0; i < real.length; i++) {
        if (this.campoDiferente(real[i], deseado[i])) return true;
      }
      return false;
    }
    if (
      typeof real === 'object' &&
      typeof deseado === 'object' &&
      real &&
      deseado
    ) {
      // Solo las keys que define la deseada (igual que diffConfigs): los campos
      // extra del real —p.ej. `_id` de subdocumentos Mongoose— NO son
      // discrepancia. Antes se usaba la unión de keys y eso flaggeaba de gusto.
      for (const key of Object.keys(deseado)) {
        if (this.campoDiferente(real[key], deseado[key])) return true;
      }
      return false;
    }
    // Misma tolerancia float que el reconciliador (diffConfigs, toleranciaFloat 1e-6)
    if (typeof real === 'number' && typeof deseado === 'number') {
      return Math.abs(real - deseado) > 1e-6;
    }
    return real !== deseado;
  }

  modoFlagDiferente(
    realModo: IModosACTIS | undefined,
    desiredModo: IModosACTIS | undefined,
    flag: keyof IModosACTIS,
  ): boolean {
    if (!this.hasVal(desiredModo)) return false;
    return (realModo?.[flag] ?? false) !== (desiredModo?.[flag] ?? false);
  }

  hayDiscrepancias(): boolean {
    if (!this.configDeseada) return false;
    if (this.tipoDispositivo === 'Luminaria GPE') {
      return (
        this.campoDiferente(
          this.configRealGeneralGPE,
          this.configDeseadaGeneralGPE,
        ) ||
        this.campoDiferente(
          this.configRealCalendarioGPE,
          this.configDeseadaCalendarioGPE,
        )
      );
    }
    if (this.tipoDispositivo === 'Luminaria ACTIS FING') {
      return this.campoDiferente(this.configRealACTIS, this.configDeseadaACTIS);
    }
    if (this.tipoDispositivo === 'Luminaria Wellness') {
      return this.campoDiferente(
        this.configRealWellness,
        this.configDeseadaWellness,
      );
    }
    return false;
  }

  // ─── porcentaje aplicado ─────────────────────────────────────

  // % del perfil realmente aplicado = ASPECTOS del config deseado que coinciden
  // con el real (cada aspecto = 1 unidad, todo-o-nada). El conteo lo hace
  // LuminariasService.porcentajePerfilAplicado (única fuente de verdad, idéntica
  // a la del listado de luminarias). Acá solo se eligen los configs por tipo.
  get porcentajeAplicado(): number | null {
    if (!this.configDeseada) return null;
    let real: unknown;
    let deseado: unknown;
    if (this.tipoDispositivo === 'Luminaria GPE') {
      real = this.configRealGPE;
      deseado = this.configDeseadaGPE;
    } else if (this.tipoDispositivo === 'Luminaria ACTIS FING') {
      real = this.configRealACTIS;
      deseado = this.configDeseadaACTIS;
    } else if (this.tipoDispositivo === 'Luminaria Wellness') {
      real = this.configRealWellness;
      deseado = this.configDeseadaWellness;
    } else {
      return null;
    }
    if (!deseado) return null;
    // Delega en el service (única fuente de verdad), así el % del modal y el del
    // listado de luminarias salen SIEMPRE iguales. Los configs ya vienen mapeados
    // por tipo; el service renormaliza la máscara ACTIS de forma idempotente.
    return this.luminariasService.porcentajePerfilAplicado(
      real,
      deseado,
      this.tipoDispositivo,
    );
  }

  get porcentajeClase(): string {
    const p = this.porcentajeAplicado;
    if (p === null) return '';
    if (p >= 100) return 'pct-ok';
    if (p >= 60) return 'pct-medio';
    return 'pct-bajo';
  }

  // (El conteo por aspecto vive en LuminariasService.porcentajePerfilAplicado,
  // única fuente de verdad compartida con el listado de luminarias.)

  // ─── GPE: calendario de dimerizado ──────────────────────────

  // Indica si el bloque de calendario tiene diferencias (habilitado, % por
  // defecto o cualquier punto). Reusa campoDiferente (deep + tolerancia float).
  get calendarioGpeDiferente(): boolean {
    return this.campoDiferente(
      this.configRealCalendarioGPE,
      this.configDeseadaCalendarioGPE,
    );
  }

  // Puntos del calendario GPE para el gráfico (mismo componente que ACTIS).
  // Los puntos GPE son por HORA del día (no offset astronómico), así que se
  // grafican en modo NO centrado (eje X 0:00..24:00) convirtiendo "HH:mm" a
  // minutos del día. Solo se grafican los puntos activos.
  get calendarioGpePuntosReal(): IPuntoDimmingChart[] {
    return this.calendarioToChart(this.configRealCalendarioGPE);
  }

  get calendarioGpePuntosDeseado(): IPuntoDimmingChart[] {
    return this.calendarioToChart(this.configDeseadaCalendarioGPE);
  }

  private calendarioToChart(
    cfg: IDimmerCalendarioConfig | null,
  ): IPuntoDimmingChart[] {
    if (!cfg?.puntos?.length) return [];
    return cfg.puntos
      .filter((p) => p.activo)
      .map((p) => ({
        offsetMinutos: this.horaAMinutos(p.hora),
        porcentaje: p.porcentaje ?? 0,
      }));
  }

  private horaAMinutos(hora: string | undefined): number {
    if (!hora) return 0;
    const [h, m] = hora.split(':').map((n) => parseInt(n, 10));
    return (h || 0) * 60 + (m || 0);
  }

  // ─── mapa coordenadas ────────────────────────────────────────

  // Objeto estable — no se recrea en cada ciclo de change detection
  _mapCfg: MapCardConfig | null = null;

  private buildMapCfg(): MapCardConfig | null {
    const geojsonReal = this.configRealACTIS?.coordenadas?.geojson;
    const geojsonDeseado = this.configDeseadaACTIS?.coordenadas?.geojson;
    const markers: MarkerMapa[] = [];
    let center: [number, number] | undefined;

    if (geojsonReal?.coordinates?.[0]) {
      const c = geojsonReal.coordinates as [number, number];
      markers.push({
        label: 'puntoSimple',
        coor: c,
        text: 'Real',
        data: { iconoId: 'pin-azul-icon', iconSize: 0.2 },
        centrar: true,
      });
      center = c;
    }
    if (geojsonDeseado?.coordinates?.[0]) {
      const c = geojsonDeseado.coordinates as [number, number];
      markers.push({
        label: 'puntoSimple',
        coor: c,
        text: 'Deseado',
        data: { iconoId: 'pin-naranja-icon', iconSize: 0.2 },
        centrar: !center,
      });
      center = center ? [(center[0] + c[0]) / 2, (center[1] + c[1]) / 2] : c;
    }
    if (markers.length === 0) return null;

    return {
      title: 'Coordenadas',
      icon: 'place',
      color: 'red',
      showShadow: false,
      height: '400px',
      mapOptions: { markers, center, zoom: markers.length > 1 ? 12 : 14 },
      nombreMap: 'MapPerfilesCoordenadas',
    };
  }

  // ─── conversión ACTIS: ICambioDimmingACTIS → IPuntoDimmingChart ─

  cambiosToChart(
    cambios: ICambioDimmingACTIS[] | undefined,
  ): IPuntoDimmingChart[] {
    if (!cambios?.length) return [];
    return cambios.map((c) => ({
      offsetMinutos: c.offsetMinutos ?? 0,
      porcentaje: Math.round(((c.nivelDimming ?? 0) / 31) * 100),
    }));
  }

  // ─── ACTIS: criterio de calendario de un perfil ─────────────

  // bit0 = domingo (protocolo §2.4.1.1.3.2), igual que el formulario de carga.
  readonly etiquetasDias = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  // Chips por día. `activo` pinta el día seleccionado. `resalta` marca los que
  // NO coinciden con `otro` y solo se usa en la fila DESEADO: señala qué hay que
  // cambiar respecto del equipo. En la fila real va sin `otro`, porque resaltar
  // en ambas filas sugería que un día inactivo estaba seleccionado.
  diasChips(
    dias: boolean[] | null,
    otro?: boolean[] | null,
  ): { letra: string; activo: boolean; resalta: boolean }[] {
    return this.etiquetasDias.map((letra, i) => {
      const activo = !!dias?.[i];
      return {
        letra,
        activo,
        resalta: !!otro && activo !== !!otro[i],
      };
    });
  }

  diasResumen(dias: boolean[] | null): string {
    if (!dias?.length) return 'Sin información';
    const NOMBRES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const sel = NOMBRES.filter((_, i) => dias[i]);
    return sel.length ? sel.join(', ') : 'Ningún día';
  }

  // El device guarda el día del año (1..366), no una fecha. Se muestra el número
  // y, entre paréntesis, a qué día calendario corresponde este año.
  formatDiaDelAnio(dia: number | null): string {
    if (!this.hasVal(dia) || !dia) return 'Sin información';
    const anio = new Date().getFullYear();
    const fecha = new Date(anio, 0, dia);
    const txt = fecha.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
    });
    return `Día ${dia} (${txt})`;
  }

  // ─── ACTIS: habilitación de perfiles ────────────────────────

  esBitHabilitado(mask: number | undefined, bit: number): boolean {
    if (!this.hasVal(mask)) return false;
    return (mask! & (1 << bit)) !== 0;
  }

  // ─── ACTIS: slots de perfiles dimming ───────────────────────

  get perfilesActisSlots(): PerfilDimmingSlot[] {
    const perfsReal = this.configRealACTIS?.perfilesDimming;
    const perfsDeseado = this.configDeseadaACTIS?.perfilesDimming;

    const definiciones = [
      {
        bit: 0,
        etiqueta: 'Todos los días 1',
        tipo: 'todosLosDias' as const,
        sdReal: undefined as FuenteCalendario,
        sdDeseado: undefined as FuenteCalendario,
        cambiosReal: perfsReal?.perfilTodosLosDias1,
        cambiosDeseado: perfsDeseado?.perfilTodosLosDias1,
      },
      {
        bit: 1,
        etiqueta: 'Todos los días 2',
        tipo: 'todosLosDias' as const,
        sdReal: undefined as FuenteCalendario,
        sdDeseado: undefined as FuenteCalendario,
        cambiosReal: perfsReal?.perfilTodosLosDias2,
        cambiosDeseado: perfsDeseado?.perfilTodosLosDias2,
      },
      {
        bit: 2,
        etiqueta: 'Días semana 1',
        tipo: 'diasSemana' as const,
        sdReal: perfsReal?.perfilDiasSemana1 as FuenteCalendario,
        sdDeseado: perfsDeseado?.perfilDiasSemana1 as FuenteCalendario,
        cambiosReal: perfsReal?.perfilDiasSemana1?.cambios,
        cambiosDeseado: perfsDeseado?.perfilDiasSemana1?.cambios,
      },
      {
        bit: 3,
        etiqueta: 'Días semana 2',
        tipo: 'diasSemana' as const,
        sdReal: perfsReal?.perfilDiasSemana2 as FuenteCalendario,
        sdDeseado: perfsDeseado?.perfilDiasSemana2 as FuenteCalendario,
        cambiosReal: perfsReal?.perfilDiasSemana2?.cambios,
        cambiosDeseado: perfsDeseado?.perfilDiasSemana2?.cambios,
      },
      {
        bit: 4,
        etiqueta: 'Día especial 1',
        tipo: 'diaEspecial' as const,
        sdReal: perfsReal?.perfilDiaEspecial1 as FuenteCalendario,
        sdDeseado: perfsDeseado?.perfilDiaEspecial1 as FuenteCalendario,
        cambiosReal: perfsReal?.perfilDiaEspecial1?.cambios,
        cambiosDeseado: perfsDeseado?.perfilDiaEspecial1?.cambios,
      },
      {
        bit: 5,
        etiqueta: 'Día especial 2',
        tipo: 'diaEspecial' as const,
        sdReal: perfsReal?.perfilDiaEspecial2 as FuenteCalendario,
        sdDeseado: perfsDeseado?.perfilDiaEspecial2 as FuenteCalendario,
        cambiosReal: perfsReal?.perfilDiaEspecial2?.cambios,
        cambiosDeseado: perfsDeseado?.perfilDiaEspecial2?.cambios,
      },
      {
        bit: 6,
        etiqueta: 'Día especial 3',
        tipo: 'diaEspecial' as const,
        sdReal: perfsReal?.perfilDiaEspecial3 as FuenteCalendario,
        sdDeseado: perfsDeseado?.perfilDiaEspecial3 as FuenteCalendario,
        cambiosReal: perfsReal?.perfilDiaEspecial3?.cambios,
        cambiosDeseado: perfsDeseado?.perfilDiaEspecial3?.cambios,
      },
      {
        bit: 7,
        etiqueta: 'Día especial 4',
        tipo: 'diaEspecial' as const,
        sdReal: perfsReal?.perfilDiaEspecial4 as FuenteCalendario,
        sdDeseado: perfsDeseado?.perfilDiaEspecial4 as FuenteCalendario,
        cambiosReal: perfsReal?.perfilDiaEspecial4?.cambios,
        cambiosDeseado: perfsDeseado?.perfilDiaEspecial4?.cambios,
      },
    ];

    return definiciones.map((d) => {
      const habilitadoReal = this.esBitHabilitado(
        perfsReal?.perfilesHabilitados,
        d.bit,
      );
      const habilitadoDeseado = perfsDeseado
        ? this.esBitHabilitado(perfsDeseado.perfilesHabilitados, d.bit)
        : null;
      const puntosReal = this.cambiosToChart(d.cambiosReal);
      const puntosDeseado = this.cambiosToChart(d.cambiosDeseado);
      // El device no mantiene habilitado un perfil SIN curva → su bit-enable
      // real queda en 0 aunque la deseada lo pida. No lo contamos como
      // discrepancia (sería irresoluble): solo comparamos el enable en perfiles
      // con curva.
      const tieneCurva = !!(d.cambiosReal?.length || d.cambiosDeseado?.length);
      const enableDiferente =
        tieneCurva &&
        habilitadoDeseado !== null &&
        habilitadoReal !== habilitadoDeseado;
      // Criterio de calendario: además de la curva, un perfil «días
      // seleccionados» define QUÉ días aplica y uno de «día especial» QUÉ día
      // del año. Divergen igual que los puntos y hay que mostrarlos.
      const diasSemanaReal = d.sdReal?.diasSemana ?? null;
      const diasSemanaDeseado = d.sdDeseado?.diasSemana ?? null;
      const diasSemanaDiferente =
        d.tipo === 'diasSemana' &&
        this.campoDiferente(diasSemanaReal, diasSemanaDeseado);

      // diaDelAnio 0 = «ningún día establecido» (valor por defecto del device),
      // así que se normaliza a null para no mostrarlo como un día real.
      const diaDelAnioReal = d.sdReal?.diaDelAnio || null;
      const diaDelAnioDeseado = d.sdDeseado?.diaDelAnio || null;
      const diaDelAnioDiferente =
        d.tipo === 'diaEspecial' &&
        this.campoDiferente(diaDelAnioReal, diaDelAnioDeseado);

      const diferente =
        this.campoDiferente(d.cambiosReal, d.cambiosDeseado) ||
        enableDiferente ||
        diasSemanaDiferente ||
        diaDelAnioDiferente;
      // El panel debe poder abrirse si hay criterio de calendario aunque no haya
      // curva: si no, una discrepancia de días/día del año quedaría invisible.
      const tieneDatos =
        puntosReal.length > 0 ||
        puntosDeseado.length > 0 ||
        this.hasVal(diasSemanaReal) ||
        this.hasVal(diasSemanaDeseado) ||
        this.hasVal(diaDelAnioReal) ||
        this.hasVal(diaDelAnioDeseado);
      return {
        etiqueta: d.etiqueta,
        bit: d.bit,
        habilitadoReal,
        habilitadoDeseado,
        puntosReal,
        puntosDeseado,
        diferente,
        tieneDatos,
        tipoCalendario: d.tipo,
        diasSemanaReal,
        diasSemanaDeseado,
        diasSemanaDiferente,
        diaDelAnioReal,
        diaDelAnioDeseado,
        diaDelAnioDiferente,
      };
    });
  }

  // ── DEBUG START ── eliminar este bloque + <div class="debug-panel"> en el HTML
  // debugGenerar(tipo: 'GPE' | 'ACTIS' | 'Wellness'): void {
  //   if (tipo === 'GPE') {
  //     const real = {
  //       mode: 'Astronómico',
  //       dimmerHabilitado: true,
  //       adrHabilitado: false,
  //       energiaExterna: true,
  //       estadoRele: true,
  //       limLuzInferior: 20,
  //       limLuzSuperior: 80,
  //       offsetGPSAmanecer: -15,
  //       offsetGPSAtardecer: 10,
  //       timeZone: 3,
  //       frecReporte: 15,
  //       dataRate: 2,
  //     };
  //     this.luminaria = {
  //       dispositivo: { tipo: 'Luminaria GPE', config: { configGeneral: real } },
  //     } as any;
  //     this.configDeseada = {
  //       tipo: 'Luminaria GPE',
  //       config: {
  //         configGeneral: {
  //           ...real,
  //           mode: 'Fotocélula',
  //           dimmerHabilitado: false,
  //           limLuzInferior: 25,
  //           frecReporte: 30,
  //         },
  //       },
  //     } as any;
  //   }
  //   if (tipo === 'ACTIS') {
  //     const geojsonReal: IGeoJSONPoint = {
  //       type: 'Point',
  //       coordinates: [-58.3816, -34.6037],
  //     };
  //     const geojsonDeseado: IGeoJSONPoint = {
  //       type: 'Point',
  //       coordinates: [-58.37, -34.6],
  //     };
  //     const real = {
  //       modoEncendido: {
  //         fotocelula: true,
  //         astronomico: false,
  //         manual: false,
  //         rele: false,
  //       },
  //       modoApagado: {
  //         fotocelula: false,
  //         astronomico: true,
  //         manual: false,
  //       },
  //       iniciarEncendida: true,
  //       fotocelula: { umbralSuperior: 800 },
  //       coordenadas: { geojson: geojsonReal, timeZone: -3 },
  //       relojAstronomico: { offsetAmanecer: -10, offsetAtardecer: 15 },
  //       configReportes: {
  //         reportarEncendidoApagado: true,
  //         reportarDimerizado: false,
  //         periodoEstado: 15,
  //         periodoEnergia: 30,
  //       },
  //       configACK: {
  //         periodoHoras: 24,
  //         forzado: false,
  //         ackEstado: true,
  //         ackEnergia: false,
  //       },
  //       perfilesDimming: {
  //         dimDefault: 15,
  //         perfilesHabilitados: 0b00011111,
  //         perfilTodosLosDias1: [
  //           { offsetMinutos: -360, nivelDimming: 31 },
  //           { offsetMinutos: 0, nivelDimming: 15 },
  //           { offsetMinutos: 300, nivelDimming: 20 },
  //         ],
  //         perfilTodosLosDias2: [
  //           { offsetMinutos: -400, nivelDimming: 25 },
  //           { offsetMinutos: 100, nivelDimming: 10 },
  //         ],
  //         perfilDiasSemana1: {
  //           cambios: <ICambioDimmingACTIS[]>[
  //             { offsetMinutos: -400, nivelDimming: 20 },
  //             { offsetMinutos: 100, nivelDimming: 8 },
  //           ],
  //         },
  //         perfilDiasSemana2: { cambios: <ICambioDimmingACTIS[]>[] },
  //         perfilDiaEspecial1: {
  //           cambios: <ICambioDimmingACTIS[]>[
  //             { offsetMinutos: -200, nivelDimming: 31 },
  //           ],
  //         },
  //         perfilDiaEspecial2: { cambios: <ICambioDimmingACTIS[]>[] },
  //         perfilDiaEspecial3: { cambios: <ICambioDimmingACTIS[]>[] },
  //         perfilDiaEspecial4: { cambios: <ICambioDimmingACTIS[]>[] },
  //       },
  //     };
  //     this.luminaria = {
  //       dispositivo: { tipo: 'Luminaria ACTIS FING', config: real },
  //     } as any;
  //     this.configDeseada = {
  //       tipo: 'Luminaria ACTIS FING',
  //       config: {
  //         ...real,
  //         modoEncendido: {
  //           fotocelula: false,
  //           astronomico: true,
  //           manual: false,
  //           rele: true,
  //         },
  //         fotocelula: { umbralInferior: 200, umbralSuperior: 800 },
  //         coordenadas: { geojson: geojsonDeseado, timeZone: -3 },
  //         configReportes: { ...real.configReportes },
  //         perfilesDimming: {
  //           ...real.perfilesDimming,
  //           perfilesHabilitados: 0b00011001,
  //           dimDefault: 20,
  //           perfilTodosLosDias1: [
  //             { offsetMinutos: -360, nivelDimming: 28 },
  //             { offsetMinutos: 0, nivelDimming: 10 },
  //             { offsetMinutos: 300, nivelDimming: 25 },
  //           ],
  //         },
  //       },
  //     } as any;
  //   }
  //   if (tipo === 'Wellness') {
  //     this.luminaria = {
  //       dispositivo: {
  //         tipo: 'Luminaria Wellness',
  //         config: { mode: 'Astronómico' },
  //       },
  //     } as any;
  //     this.configDeseada = {
  //       tipo: 'Luminaria Wellness',
  //       config: { mode: 'Fotocélula' },
  //     } as any;
  //   }
  //   this._mapCfg = this.buildMapCfg();
  // }
  // ── DEBUG END ──

  get dimDefaultRealPct(): number | null {
    const d = this.configRealACTIS?.perfilesDimming?.dimDefault;
    return this.hasVal(d) ? Math.round((d! / 31) * 100) : null;
  }

  get dimDefaultDeseadoPct(): number | null {
    const d = this.configDeseadaACTIS?.perfilesDimming?.dimDefault;
    return this.hasVal(d) ? Math.round((d! / 31) * 100) : null;
  }
}
